import React, { useEffect, useState } from 'react';
import { GroupCalendar } from '../../calendar/group-calendar/group-calendar';
import { Event } from '../../calendar/models/event';
import { TimelineEvent } from '../../slices/marketplace/timeline/models/timeline-event.model';
import { EventActionType } from '../../calendar/models/eventAction';
import {
    TimelineParty,
    TimelinePartyColors,
} from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import './MarketplaceCalendar.scss';
import { AddEventsToAnotherCalendarModal } from '../../calendar/add-events-to-another-calendar-modal/add-events-to-another-calendar-modal';
import { useStateSelector } from '../../store/selectors';
import { USER_ROLES } from '../../utils/constants';

interface MarketplaceCalendarProps {
    timelineEvents: TimelineEvent[];
    startDate: Date;
    timelineId: number;
    onEventClick(eventId: string): void;
    canUserRedirectToAction: (eventId: string) => boolean;
}

export const MarketplaceCalendar = (props: MarketplaceCalendarProps) => {
    const summary = useStateSelector((s) => s.userSummary.summary);
    const [currentDate, setCurrentDate] = useState<Date>();
    const [events, setEvents] = useState<Event[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<Event | undefined>();
    const [
        isAddEventsToAnotherCalendarModalVisible,
        setIsAddEventsToAnotherCalendarModalVisible,
    ] = useState<boolean>(false);

    const getMarketplaceParty = (): TimelineParty => {
        if (summary.isMarketplaceBuyer) {
            return TimelineParty.Buyer;
        }
        if (summary?.roleId === USER_ROLES.ADMIN) {
            return TimelineParty.VetValue;
        }
        return TimelineParty.Seller;
    };

    useEffect(() => {
        const events = props.timelineEvents.map((event) => {
            const eventDate = new Date(props.startDate);
            eventDate.setDate(eventDate.getDate() + event.dayStart);
            const eventDeadline = new Date(props.startDate);
            eventDeadline.setDate(eventDeadline.getDate() + event.dayEnd);
            const actions = [];
            actions.push({
                id: 0,
                name: event.name,
                message: '',
                date: eventDeadline,
                type: EventActionType.Deadline,
                isAllDayLong: true,
            });
            const reminderDate = new Date(eventDeadline);
            reminderDate.setDate(reminderDate.getDate() - 2);
            const eventDateWithoutTime = new Date(eventDate.toDateString());
            const reminderDateWithoutTime = new Date(
                reminderDate.toDateString()
            );
            if (reminderDateWithoutTime > eventDateWithoutTime) {
                actions.push({
                    id: 0,
                    name: event.name,
                    message: '',
                    date: reminderDate,
                    type: EventActionType.Reminder,
                    isAllDayLong: true,
                });
            }
            const colors = TimelinePartyColors.find(
                (t) => t.id === event.party
            );

            const userActionBeClicked = props.canUserRedirectToAction(event.id);
            const onClick = () => {
                if (userActionBeClicked) {
                    props.onEventClick(event.id);
                }
            };

            return new Event(
                event.id,
                0,
                '',
                event.name,
                '',
                eventDate,
                eventDate,
                true,
                [],
                actions,
                [],
                'event',
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                {
                    color: userActionBeClicked ? '#545cd8' : colors.color,
                    backgroundColor: colors.backgroundColor,
                    activeColor: colors.activeColor,
                    activeBackgroundColor: colors.activeBackgroundColor,
                },
                onClick
            );
        });
        setEvents(events);
    }, [props.timelineEvents]);

    return (
        <div className="marketplace-calendar">
            <div className="pmg-calender-wrapper">
                <div className="col-title">
                    <h4>Calendar</h4>
                    <div
                        onClick={() => {
                            setIsAddEventsToAnotherCalendarModalVisible(true);
                        }}>
                        Export Events to Personal Calendar(s)
                    </div>
                </div>
            </div>
            <GroupCalendar
                events={events}
                currentDate={currentDate}
                setCurrentDate={setCurrentDate}
                setDetailsEventActions={() => {}}
                setDetailsEventActionType={() => {}}
                setSelectedEvent={setSelectedEvent}
                selectedEvent={selectedEvent}
                setShowDetailsModalEventAction={() => {}}
                groupId={0}
                notificationFlagsOnPreviousPages={0}
                notificationFlagsOnNextPages={0}
                isInitializingCalendarPage={false}></GroupCalendar>
            {isAddEventsToAnotherCalendarModalVisible ? (
                <AddEventsToAnotherCalendarModal
                    isVisible={isAddEventsToAnotherCalendarModalVisible}
                    setIsVisible={setIsAddEventsToAnotherCalendarModalVisible}
                    calendarId={props.timelineId.toString()}
                    header={
                        'Add your Engagement Calendar to your personal calendar'
                    }
                    link={`marketplace/${props.timelineId}/calendar/export/ics`}></AddEventsToAnotherCalendarModal>
            ) : (
                []
            )}
        </div>
    );
};
